/**
 * Client-side filtering of the loaded municipality.
 *
 * Mirrors backend/domain/buildings.py filter_features. Both exist on purpose:
 * the server filters a fresh request (deep links, the `?letra=` query), and this
 * filters the collection already in memory, so toggling a letter does not
 * refetch ten megabytes of GeoJSON.
 *
 * Because they are two implementations of one rule, the shared meaning lives in
 * lettersForDeadline — which both sides derive from — rather than in a list of
 * letters repeated here.
 */

import type { Feature } from "geojson";

import { lettersForDeadline } from "./letters";

export interface BuildingFilters {
  /** Letters currently switched on. Empty means "show nothing". */
  letters: ReadonlySet<string>;
  /** EED obligation year, or null for no deadline filter. */
  deadline: number | null;
  /** Catastro use class, or null for all. */
  uso?: string | null;
}

/**
 * A building's letter, or null when the pipeline could not classify it.
 *
 * Unclassified buildings are grey on the map and cannot satisfy a letter filter.
 * Treating them as matching would put grey polygons inside a "show me only F and
 * G" view, which reads as a rendering bug.
 */
function letterOf(feature: Feature): string | null {
  const raw = feature.properties?.["letra"];
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

export function matches(feature: Feature, filters: BuildingFilters): boolean {
  const letter = letterOf(feature);

  if (!letter || !filters.letters.has(letter)) return false;

  if (filters.deadline != null) {
    // Intersected with the letter filter rather than replacing it: the two
    // controls sit side by side, and a user who has switched off every letter
    // but D and then picks "before 2030" is asking for the overlap, which is
    // empty. Silently widening back to F–G would answer a different question.
    if (!lettersForDeadline(filters.deadline).has(letter as never)) return false;
  }

  if (filters.uso && feature.properties?.["uso"] !== filters.uso) return false;

  return true;
}

export function filterFeatures(
  features: readonly Feature[],
  filters: BuildingFilters,
): Feature[] {
  return features.filter((f) => matches(f, filters));
}

/** True when the filters would show everything, so the fast path can be used. */
export function isUnfiltered(
  filters: BuildingFilters,
  allLetters: readonly string[],
): boolean {
  return (
    filters.deadline == null &&
    !filters.uso &&
    allLetters.every((l) => filters.letters.has(l))
  );
}
